import {
  COMMERCIAL_MODEL,
  ROW_KIND,
  INHERITANCE_SOURCE,
  PRICE_OPERATION,
  PRICE_TYPE,
  DERIVED_FROM_TYPE,
  PMS_CRS_LABEL,
  DERIVED_RATE_STATUS,
} from "./constants";

const safeNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const formatPriceConfigItem = (item, currencyCode) => {
  const sign = item.priceOperation === PRICE_OPERATION.SUBTRACTION ? "-" : "+";
  const deltas = Object.values(item.priceDelta || {}).map(safeNumber);
  const maxDelta = deltas.length ? Math.max(...deltas.map(Math.abs)) : 0;
  if (item.priceType === PRICE_TYPE.PERCENT) {
    return `${sign}${maxDelta}%`;
  }
  const currency = currencyCode || "USD";
  return `${sign}${maxDelta.toFixed(2)} ${currency}`;
};

// V1 spec: show only the first own price-config entry, optionally chained to
// the first inherited entry ("own on inherited"). Multi-entry price configs
// collapse to the first by `order` — full breakdown lives in the detail view.
export const formatPricingSummary = (
  priceConfig,
  ownOnly = true,
  currencyCode
) => {
  if (!Array.isArray(priceConfig) || priceConfig.length === 0) return "-";
  const items = ownOnly
    ? priceConfig.filter((pc) => !pc.derivedFromId)
    : priceConfig;
  if (items.length === 0) return "-";
  const ordered = [...items].sort(
    (a, b) => safeNumber(a.order) - safeNumber(b.order)
  );
  const own = ordered.filter((pc) => !pc.derivedFromId);
  const inherited = ordered.filter((pc) => pc.derivedFromId);
  const ownStr = own.map((pc) => formatPriceConfigItem(pc, currencyCode))[0];
  if (!ownStr) return "-";
  if (inherited.length === 0) return ownStr;
  const inheritedStr = formatPriceConfigItem(inherited[0], currencyCode);
  return `${ownStr} on ${inheritedStr.replace(/^[+-]/, "")}`;
};

export const resolveBaseRateplanIds = (derivedRates) => {
  if (!derivedRates?.length) return [];
  const drById = new Map(derivedRates.map((dr) => [dr.id, dr]));
  return derivedRates.map((dr) => {
    if (dr.baseRateplanId) return dr;
    let cursor = dr.derivedFrom;
    const visited = new Set();
    while (cursor && !visited.has(cursor)) {
      visited.add(cursor);
      const parent = drById.get(cursor);
      if (!parent) return { ...dr, baseRateplanId: cursor };
      if (parent.baseRateplanId) {
        return { ...dr, baseRateplanId: parent.baseRateplanId };
      }
      cursor = parent.derivedFrom;
    }
    return dr;
  });
};

// A derived rate is Active only when the API says so explicitly. Rows missing
// both `isActive` and `status` default to Inactive — treating unknown shapes
// as Active would be a silent data-correctness risk for live channels.
const isActiveDerived = (dr) =>
  dr.isActive === true || dr.status === DERIVED_RATE_STATUS.ACTIVE;

// Base rate plans come from METADATA/v2/metadata/rateplan/search, which
// returns distributionType at the root of each rate plan — the single source
// of truth, no client-side derivation. A row without the field gets null and
// renders "-", rather than a guessed value. (Derived rates use a different
// endpoint and field — see distributionTypeForDerived.)
const distributionTypeForBase = (base) =>
  base.distributionType?.toUpperCase?.() ?? null;

// Commercial model only drives the Prebuy badge colour (Prebuy pairs with both
// models). Anything other than DIRECT reads as COMMUNITY, matching Global
// Controls. Base rate plans may carry it at the root or under additionalData.
const commercialModelFor = (rateplanOrDerived) =>
  (rateplanOrDerived?.commercialModel ??
    rateplanOrDerived?.additionalData?.commercialModel) ===
  COMMERCIAL_MODEL.DIRECT
    ? COMMERCIAL_MODEL.DIRECT
    : COMMERCIAL_MODEL.COMMUNITY;

// Derived rates (METADATA/v2/metadata/derived-rateplan) own their
// distribution only when distributionInheritance.source is OVERRIDDEN — then
// `distributionConfig` holds both the distribution value and the client ids.
// When INHERITED the API omits `distributionConfig` entirely and the rate
// mirrors its parent, so walk up the derivation chain — parent derived rates
// first, ending at the base rate plan. Both the Distribution Type badge and
// the Buyers column read from the same resolved owner so they can never
// disagree. Returns { derived } | { base } | {}. No derivation from
// isExclusive/commercialModel: those are separate concepts (a rate can be
// isExclusive with DIRECT distribution).
const resolveDistributionOwner = (dr, drById, baseById) => {
  const seen = new Set();
  let current = dr;
  while (current) {
    const isInherited =
      current.distributionInheritance?.source === INHERITANCE_SOURCE.INHERITED;
    if (!isInherited && current.distributionConfig) {
      return { derived: current };
    }
    if (current.id != null) {
      if (seen.has(current.id)) break;
      seen.add(current.id);
    }
    if (current.derivedFromType === DERIVED_FROM_TYPE.DERIVED_RATE) {
      current = drById?.get?.(current.derivedFrom);
    } else {
      const base = baseById?.get?.(current.baseRateplanId);
      return base ? { base } : {};
    }
  }
  return {};
};

const distributionTypeForDerived = (dr, drById, baseById) => {
  const owner = resolveDistributionOwner(dr, drById, baseById);
  if (owner.derived) {
    return (
      owner.derived.distributionConfig?.distribution?.toUpperCase?.() ?? null
    );
  }
  if (owner.base) return distributionTypeForBase(owner.base);
  return null;
};

// Same owner as the distribution type: a derived rate that inherits its
// distribution must also inherit the commercial model from whoever owns it, or
// an inherited Direct Prebuy would render in the Community Prebuy colour. Falls
// back to the rate's own field only when the chain cannot be resolved.
const commercialModelForDerived = (dr, drById, baseById) => {
  const owner = resolveDistributionOwner(dr, drById, baseById);
  return commercialModelFor(owner.derived ?? owner.base ?? dr);
};

const cancellationLabelForDerived = (dr, baseById) => {
  const own = dr?.contractDefinition?.refundable;
  const inherited = dr?.contractInheritance?.refundable;
  const refundable = own === undefined || own === null ? inherited : own;
  if (refundable !== undefined && refundable !== null) {
    return refundable ? "Ref" : "Non-Ref";
  }
  // Final fallback: inherit from the base rateplan (Figma shows derived rates
  // displaying the inherited cancellation policy when they don't override it).
  const base = baseById?.get?.(dr?.baseRateplanId);
  if (base) {
    return refundableLabelForBase(base);
  }
  return "-";
};

// V1 spec: base rows display short "Ref" / "Non-Ref" text only — no full
// policy names. The rateplan search API's boolean `isRefundable` flag is the
// single source of truth; rows without it show "-".
const refundableLabelForBase = (rp) => {
  if (typeof rp?.isRefundable === "boolean") {
    return rp.isRefundable ? "Ref" : "Non-Ref";
  }
  return "-";
};

// Base rate plans expose buyers as `exclusiveClients`; derived rates expose
// them as `distributionConfig.clientIds`. Callers should go through this
// helper rather than reading the row directly so the asymmetry stays in one
// place.
export const getClientIdsForRow = (row) => {
  if (Array.isArray(row?.distributionConfig?.clientIds)) {
    return row.distributionConfig.clientIds;
  }
  if (Array.isArray(row?.exclusiveClients)) {
    return row.exclusiveClients
      .map((c) => c?.clientId ?? c?.id)
      .filter((id) => id != null);
  }
  return [];
};

const clientsForDerived = (dr, drById, baseById, clientsById) => {
  const owner = resolveDistributionOwner(dr, drById, baseById);
  // Mirror exactly what the owning row displays: a base rate plan shows its
  // exclusiveClients verbatim, a derived owner shows its resolved clientIds.
  if (owner.base) return owner.base.exclusiveClients || [];
  if (!owner.derived) return [];
  const ids = getClientIdsForRow(owner.derived);
  if (!ids.length) return [];
  return ids.map((id) => {
    const client = clientsById?.get?.(id);
    return {
      clientId: id,
      clientName: client?.clientName || client?.name || `Client #${id}`,
    };
  });
};

const derivedFromLabel = (dr, baseByCode, drById) => {
  if (!dr?.derivedFrom) return PMS_CRS_LABEL;
  if (dr.derivedFromType === DERIVED_FROM_TYPE.BASE_RATEPLAN) {
    const base = baseByCode.get(dr.derivedFrom);
    return base?.code || base?.name || `#${dr.derivedFrom}`;
  }
  const parent = drById.get(dr.derivedFrom);
  return (
    parent?.crsRateplanCode ||
    parent?.code ||
    parent?.name ||
    `#${dr.derivedFrom}`
  );
};

const appendDerivedRows = ({
  rows,
  childrenByParent,
  parentId,
  depth,
  expandedIds,
  drById,
  baseByCode,
  clientsById,
  ancestorsContinuing,
  maxDepth,
}) => {
  const children = childrenByParent.get(parentId) || [];

  children.forEach((dr, idx) => {
    const grandChildren = childrenByParent.get(dr.id) || [];
    // At the depth cap a row's children are not displayable: report it as
    // childless so no expand chevron renders and recursion stops.
    const hasChildren = grandChildren.length > 0 && depth < maxDepth;
    const isExpanded = expandedIds.has(dr.id);
    const isLastSibling = idx === children.length - 1;

    // Ancestor rails this row must draw: for each ancestor depth D < depth-1,
    // include D if the ancestor at depth D+1 in our lineage still has more
    // siblings after our subtree (so the rail visually continues past us).
    const ancestorRailDepths = [];
    for (let d = 0; d < depth - 1; d += 1) {
      if (ancestorsContinuing[d]) ancestorRailDepths.push(d);
    }

    rows.push({
      kind: ROW_KIND.DERIVED,
      depth,
      parentId,
      hasChildren,
      isExpanded,
      isFirstSibling: idx === 0,
      isLastSibling,
      ancestorRailDepths,
      isGapAbove: false, // Calculated later by recalculateGaps on visible rows
      id: dr.id,
      code: dr.outboundCode || dr.crsRateplanCode || `#${dr.id}`,
      name: dr.name || "-",
      isActive: isActiveDerived(dr),
      exclusiveClients: clientsForDerived(dr, drById, baseByCode, clientsById),
      pricing: formatPricingSummary(dr.priceConfig, false),
      derivedFromLabel: derivedFromLabel(dr, baseByCode, drById),
      distributionType: distributionTypeForDerived(dr, drById, baseByCode),
      commercialModel: commercialModelForDerived(dr, drById, baseByCode),
      cancellationLabel: cancellationLabelForDerived(dr, baseByCode),
      isVisible: true, // Initial visibility state
      raw: dr,
    });

    // Always traverse children regardless of expansion state
    // Visibility will be controlled separately
    if (hasChildren) {
      // For child traversal: mark current depth-1 as "continuing" iff we have
      // more siblings after this row (children should draw an ancestor rail
      // at our parent's chevron column for us).
      const childAncestors = ancestorsContinuing.slice();
      childAncestors[depth - 1] = !isLastSibling;
      appendDerivedRows({
        rows,
        childrenByParent,
        parentId: dr.id,
        depth: depth + 1,
        expandedIds,
        drById,
        baseByCode,
        clientsById,
        ancestorsContinuing: childAncestors,
        maxDepth,
      });
    }
  });
};

// `maxDepth` caps how deep the tree is built (rows at depth > maxDepth are
// omitted and rows at the cap report hasChildren: false). Display trees pass
// MAX_VISIBLE_DEPTH; the Excel-export tree omits it to include every level.
export const buildFlatTree = ({
  baseRateplans,
  derivedRates,
  expandedIds,
  clientsById,
  maxDepth = Infinity,
}) => {
  const rows = [];
  const baseById = new Map(baseRateplans.map((rp) => [rp.id, rp]));
  const resolved = resolveBaseRateplanIds(derivedRates || []);
  const drById = new Map(resolved.map((dr) => [dr.id, dr]));

  const childrenByParent = new Map();

  resolved.forEach((dr) => {
    if (dr.derivedFromType === DERIVED_FROM_TYPE.BASE_RATEPLAN) {
      const list = childrenByParent.get(dr.baseRateplanId) || [];
      list.push(dr);
      childrenByParent.set(dr.baseRateplanId, list);
    } else if (dr.derivedFromType === DERIVED_FROM_TYPE.DERIVED_RATE) {
      const list = childrenByParent.get(dr.derivedFrom) || [];
      list.push(dr);
      childrenByParent.set(dr.derivedFrom, list);
    } else {
      const list = childrenByParent.get(dr.baseRateplanId) || [];
      list.push(dr);
      childrenByParent.set(dr.baseRateplanId, list);
    }
  });

  baseRateplans.forEach((rp) => {
    const directChildren = childrenByParent.get(rp.id) || [];
    const hasChildren = directChildren.length > 0 && maxDepth > 0;
    const isExpanded = expandedIds.has(rp.id);

    rows.push({
      kind: ROW_KIND.BASE,
      depth: 0,
      parentId: null,
      hasChildren,
      isExpanded,
      isFirstSibling: true,
      isLastSibling: true,
      isGapAbove: false, // Calculated later by recalculateGaps on visible rows
      id: rp.id,
      code: rp.code || `#${rp.id}`,
      name: rp.name || "-",
      isActive: !!rp.isActive,
      exclusiveClients: rp.exclusiveClients || [],
      pricing: null,
      derivedFromLabel: PMS_CRS_LABEL,
      distributionType: distributionTypeForBase(rp),
      commercialModel: commercialModelFor(rp),
      cancellationLabel: refundableLabelForBase(rp),
      isVisible: true, // Initial visibility state
      raw: rp,
    });

    // Always traverse children regardless of expansion state
    // Visibility will be controlled separately
    if (hasChildren) {
      appendDerivedRows({
        rows,
        childrenByParent,
        parentId: rp.id,
        depth: 1,
        expandedIds,
        drById,
        baseByCode: baseById,
        clientsById,
        ancestorsContinuing: [],
        maxDepth,
      });
    }
  });

  // Note: isGapAbove is initialized to false for all rows.
  // Gaps are calculated by recalculateGaps() on the visible row sequence
  // (after expansion and filtering), not here on the full tree.
  return rows;
};

// The derived-rateplan list endpoint may return either:
//   - a flat array: [ApiDerivedRate, ...] (per API schema doc), or
//   - GC's grouped shape: { derivedRates: [{ baseRateplanId, baseRateplanCode, rates: [...] }] }
// Normalize to a flat array of derived-rate objects with baseRateplanId set
// when the grouped shape provides it at the group level. If neither rate-level
// nor group-level baseRateplanId is present we log and skip the row rather
// than silently anchoring it under id 0 (which would clump orphans together).
export const flattenDerivedRatesResponse = (payload) => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload.derivedRates)) {
    const flattened = payload.derivedRates.flatMap((group) => {
      const rates = Array.isArray(group?.rates) ? group.rates : [];
      return rates
        .map((r) => {
          const baseId = r?.baseRateplanId ?? group?.baseRateplanId;
          if (baseId == null) {
            globalThis.R7Insight?.error?.(
              "Derived rate missing baseRateplanId; dropping",
              { rateId: r?.id }
            );
            return null;
          }
          return { ...r, baseRateplanId: baseId };
        })
        .filter(Boolean);
    });

    return flattened;
  }

  if (Array.isArray(payload.content)) {
    return payload.content;
  }

  return [];
};

// Demand clients arrive from at least two endpoints with different keys: the
// admin client API uses `id`, the legacy supplier API uses `clientId`. Indexing
// under both lets `clientsById.get(idFromRate)` resolve regardless of which
// shape produced the rate. They are not expected to collide — a single client
// record carries one or the other, not both with different values.
export const buildClientsById = (clients) => {
  const map = new Map();
  if (!Array.isArray(clients)) return map;
  clients.forEach((c) => {
    if (c?.id != null) map.set(c.id, c);
    if (c?.clientId != null) map.set(c.clientId, c);
  });
  return map;
};

/**
 * Apply expansion state to control visibility of child nodes.
 * Children of collapsed parents are marked as not visible.
 * @param {Array} rows - Complete tree with all nodes
 * @returns {Array} Tree with visibility updated based on expansion state
 */
export const applyExpansionState = (rows) => {
  if (!rows || rows.length === 0) return [];

  // Build parent-child relationship map
  const childrenMap = new Map();
  const rowsById = new Map();

  rows.forEach((row) => {
    rowsById.set(row.id, row);
    if (row.parentId !== null && row.parentId !== undefined) {
      const siblings = childrenMap.get(row.parentId) || [];
      siblings.push(row.id);
      childrenMap.set(row.parentId, siblings);
    }
  });

  // Determine which nodes should be visible based on expansion state
  const visibleIds = new Set();

  // Recursively mark visible nodes
  const markVisibleRecursive = (nodeId) => {
    visibleIds.add(nodeId);
    const node = rowsById.get(nodeId);
    if (node?.isExpanded && node?.hasChildren) {
      const children = childrenMap.get(nodeId) || [];
      children.forEach((childId) => markVisibleRecursive(childId));
    }
  };

  // Start with all base rateplans (depth 0)
  rows.forEach((row) => {
    if (row.depth === 0) {
      markVisibleRecursive(row.id);
    }
  });

  // Update visibility on all rows
  return rows.map((row) => ({
    ...row,
    isVisible: visibleIds.has(row.id),
  }));
};

// Helper function to check if a row matches the filters
const rowMatchesFilters = (row, filters) => {
  const {
    searchQuery,
    distributionFilter,
    buyerFilter,
    cancellationFilter,
    statusFilter,
  } = filters;

  // Search filter (searches in code and name). The query is matched as-is —
  // no trimming — to align with the other property-config pages, which send
  // the raw input to the search API.
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    const code = (row.code || "").toLowerCase();
    const name = (row.name || "").toLowerCase();
    if (!code.includes(query) && !name.includes(query)) {
      return false;
    }
  }

  // Distribution Type filter (multi-select; empty selection = all). A row
  // without a distributionType matches no selection — it only shows when the
  // filter is empty.
  if (distributionFilter?.length > 0) {
    const rowDist = row.distributionType?.toUpperCase();
    const matchesDist = distributionFilter.some(
      (dist) => dist.toUpperCase() === rowDist
    );
    if (!matchesDist) return false;
  }

  // Buyer filter (multi-select; empty selection = all)
  if (buyerFilter?.length > 0) {
    if (
      !row.exclusiveClients ||
      !Array.isArray(row.exclusiveClients) ||
      row.exclusiveClients.length === 0
    ) {
      return false;
    }
    const hasBuyer = row.exclusiveClients.some((client) =>
      buyerFilter.includes(client.clientId)
    );
    if (!hasBuyer) return false;
  }

  // Cancellation Policy filter
  if (cancellationFilter && cancellationFilter !== "all") {
    const label = (row.cancellationLabel || "").toLowerCase();
    if (cancellationFilter.toLowerCase() === "refundable") {
      if (!label.includes("ref") || label.includes("non")) return false;
    } else if (cancellationFilter.toLowerCase() === "non-refundable") {
      if (!label.includes("non")) return false;
    }
  }

  // Status filter
  if (statusFilter && statusFilter !== "all") {
    if (statusFilter === "active" && !row.isActive) return false;
    if (statusFilter === "inactive" && row.isActive) return false;
  }

  return true;
};

/**
 * Recalculate gap flags based on visible row sequence.
 * Gaps should only appear between:
 * - First derived child under expanded base parent
 * - Next base row after expanded subtree
 * @param {Array} visibleRows - Array of visible rows only
 * @returns {Array} Rows with correct isGapAbove flags
 */
export const recalculateGaps = (visibleRows) => {
  if (!visibleRows || visibleRows.length === 0) return [];

  return visibleRows.map((row, i) => {
    const prev = visibleRows[i - 1];
    if (!prev) {
      return { ...row, isGapAbove: false };
    }

    const prevIsBaseExpanded =
      prev.kind === ROW_KIND.BASE && prev.isExpanded && prev.hasChildren;
    const prevIsDerived = prev.kind === ROW_KIND.DERIVED;
    const isDerived = row.kind === ROW_KIND.DERIVED;
    const isBase = row.kind === ROW_KIND.BASE;

    const isGapAbove =
      (Boolean(prevIsBaseExpanded) && isDerived) ||
      (Boolean(prevIsDerived) && isBase);

    return {
      ...row,
      isGapAbove,
    };
  });
};

/**
 * Apply filters to tree and update visibility and disabled state.
 * - Rows that don't match filters are marked as not visible (unless they have matching descendants)
 * - Parents that don't match but have matching descendants are marked as disabled (greyed out)
 * - Respects existing visibility from expansion state (doesn't re-show collapsed children)
 *
 * Performance: Uses single-pass DFS with memoization (O(n) instead of O(n²·d)).
 *
 * @param {Array} rows - Complete tree with all nodes and expansion visibility applied
 * @param {Object} filters - Filter criteria
 * @returns {Array} Tree with filter visibility and disabled state applied
 */
export const applyFiltersToTree = (rows, filters) => {
  if (!rows || rows.length === 0) return [];

  // Build maps for efficient lookup
  const childrenMap = new Map();
  const rowsById = new Map();

  rows.forEach((row) => {
    rowsById.set(row.id, row);
    if (row.parentId !== null && row.parentId !== undefined) {
      const siblings = childrenMap.get(row.parentId) || [];
      siblings.push(row.id); // Store IDs for memory efficiency
      childrenMap.set(row.parentId, siblings);
    }
  });

  // Caches: computed in single bottom-up DFS pass
  const selfMatchesCache = new Map();
  const treeMatchesCache = new Map();

  // Post-order DFS: compute match results for subtree rooted at nodeId.
  // Each node visited exactly once; results cached for O(1) lookup.
  const computeMatches = (nodeId) => {
    // Return cached result if already computed
    if (treeMatchesCache.has(nodeId)) {
      return treeMatchesCache.get(nodeId);
    }

    const row = rowsById.get(nodeId);
    if (!row) {
      selfMatchesCache.set(nodeId, false);
      treeMatchesCache.set(nodeId, false);
      return false;
    }

    // Check if this node matches filters
    const selfMatches = rowMatchesFilters(row, filters);
    selfMatchesCache.set(nodeId, selfMatches);

    // Check if any descendant matches (post-order: compute children first)
    // IMPORTANT: Must process ALL children to populate cache, not stop at first match
    const childIds = childrenMap.get(nodeId) || [];
    let anyDescendantMatches = false;
    childIds.forEach((childId) => {
      if (computeMatches(childId)) {
        anyDescendantMatches = true;
      }
    });

    // Tree matches if self or any descendant matches
    const treeMatches = selfMatches || anyDescendantMatches;
    treeMatchesCache.set(nodeId, treeMatches);

    return treeMatches;
  };

  // Trigger DFS for all root nodes (depth 0) to populate caches
  rows.forEach((row) => {
    if (row.depth === 0) {
      computeMatches(row.id);
    }
  });

  // Apply cached results to update visibility and disabled state
  return rows.map((row) => {
    const selfMatches = selfMatchesCache.get(row.id) ?? false;
    const treeMatches = treeMatchesCache.get(row.id) ?? false;

    // Disabled if tree has matches but self doesn't (descendant matches only)
    const isDisabled = treeMatches && !selfMatches;

    // Visible if tree matches AND was already visible from expansion state
    const isVisible = treeMatches && row.isVisible;

    return {
      ...row,
      isVisible,
      isDisabled,
    };
  });
};
